import { Project } from '@griply/assets';
import { GoalType } from '@griply/types';
import {
  getGoalStatus,
  getGoalTargetLabel,
  getGoalUnitFormat,
  GoalTargetLabelKind,
  hasGoalReachedTarget,
} from '@griply/utils';
import {
  useGoalById,
  useGoalCompletion,
  useGoals,
  useUpdateGoal,
} from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { BusinessChart } from 'shared/assets/icons';
import {
  Colors as ButtonColors,
  Sizes as ButtonSizes,
  Variants as ButtonVariants,
} from 'shared/components/ui/button';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import {
  EditableTextField,
  Variants,
} from 'shared/components/ui/editable-text-field';
import { GoalStatusChip } from 'shared/components/ui/goal-status-chip';
import { Icon } from 'shared/components/ui/icon';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useCompleteGoal } from 'shared/contexts/complete-goal';
import { useOpenGoalMetricsForm } from 'shared/contexts/goal-metrics-form';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { GoalDetailIllustration } from '../goal-detail-illustration';
import { DeadlinePickerRow } from './deadline-picker-row';
import { GoalDetailDescription } from './goal-detail-description';
import * as Styled from './goal-detail-header.style';
import { ImpactPickerRow } from './impact-picker-row';
import { MetaRow } from './meta-row';
import { StartDatePickerRow } from './start-date-picker-row';

export type GoalDetailHeaderProps = {};

export const GoalDetailHeader: React.FC<GoalDetailHeaderProps> = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const goal = useGoalById(id);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const locale = useI18nLocale();
  const numberFormat = useNumberFormat();
  const { submit: updateGoal } = useUpdateGoal();
  const openGoalMetricsForm = useOpenGoalMetricsForm();
  const completeGoal = useCompleteGoal();
  const taskCounts = useTaskCounts();
  const completion = useGoalCompletion(id);
  const progressValueRef = useRef<HTMLSpanElement>(null);

  const color = useMemo(
    () =>
      goal ? getLinkedItemColor(goal?.id, { goals, lifeAreas }) : undefined,
    [goal, goals, lifeAreas],
  );

  if (!goal) {
    return null;
  }

  const isProject = goal.type === GoalType.Project;
  const isEditable = !goal.archivedAt && !goal.frozenAt;
  const goalStatus = getGoalStatus(goal);

  const onChangeTitle = (title: string) =>
    isEditable && updateGoal({ id: goal.id, name: title });

  const onChangeGoalDescription = (description: string) =>
    isEditable && updateGoal({ id: goal.id, description });

  const onTargetClick = () => openGoalMetricsForm(goal);

  const onCompleteProject = () => completeGoal(goal);

  // Same gate the goal completion banner uses — projects get this button instead.
  const canCompleteProject =
    isProject &&
    !goal.completedAt &&
    hasGoalReachedTarget(goal, goals, taskCounts?.byGoalProgress);

  const unitFormat = getGoalUnitFormat(goal.metric, locale);
  const targetLabel = getGoalTargetLabel(goal.metric, {
    unitFormat,
    numberFormat,
  });
  const targetText =
    targetLabel.kind === GoalTargetLabelKind.Value
      ? targetLabel.text
      : targetLabel.kind === GoalTargetLabelKind.CompletedTasks
        ? t('pages.goal-detail.header.metric.completed-tasks')
        : targetLabel.kind === GoalTargetLabelKind.SubGoalProgress
          ? t('pages.goal-detail.header.metric.subgoal-progress')
          : targetLabel.kind === GoalTargetLabelKind.CompletedSubGoals
            ? t('pages.goal-detail.header.metric.completed-subgoals')
            : t('pages.goal-detail.header.target.placeholder');

  return (
    <>
      <PageSectionBase noPadding>
        <Styled.Header>
          <Styled.TopRow>
            <Styled.Body $completed={!!goal.completedAt}>
              <Styled.TitleContainer>
                <EditableTextField
                  value={goal.name}
                  onChange={onChangeTitle}
                  variant={Variants.H4}
                  isStrikeThrough={!!goal.completedAt}
                />
                {!!goalStatus && (
                  <Styled.ChipContainer>
                    <GoalStatusChip status={goalStatus} />
                  </Styled.ChipContainer>
                )}
              </Styled.TitleContainer>
              <Styled.DescriptionContainer>
                <GoalDetailDescription
                  value={goal.description}
                  onChange={onChangeGoalDescription}
                  disabled={!!goal.archivedAt || !!goal.frozenAt}
                />
              </Styled.DescriptionContainer>
            </Styled.Body>

            <Styled.IllustrationContainer>
              <Styled.ImageContainer $isProject={isProject}>
                {isProject ? (
                  <Styled.ProjectIcon $color={color ?? undefined}>
                    <Icon icon={Project} />
                  </Styled.ProjectIcon>
                ) : (
                  <GoalDetailIllustration
                    illustration={goal.image ?? goal.iconName}
                  />
                )}
              </Styled.ImageContainer>
              {!!color && !isProject && <Styled.ColorDot $color={color} />}
            </Styled.IllustrationContainer>
          </Styled.TopRow>

          <Styled.MetaContainer>
            {isProject && (
              <>
                <MetaRow
                  ref={progressValueRef}
                  iconElement={
                    <CircleProgress
                      percentage={completion.percentage}
                      hidePercentage
                      isSmall
                    />
                  }
                  label={t('pages.goal-detail.header.progress.label')}
                >
                  {`${completion.percentage}%`}

                  {canCompleteProject && (
                    <Styled.CompleteButton
                      variant={ButtonVariants.Contained}
                      color={ButtonColors.Success}
                      size={ButtonSizes.Small}
                      onClick={onCompleteProject}
                    >
                      {t('general.complete')}
                    </Styled.CompleteButton>
                  )}
                </MetaRow>
                {!!completion.end && (
                  <Tooltip
                    referenceElement={progressValueRef}
                    label={t('pages.goal-detail.header.progress.tooltip', {
                      completed: completion.completed,
                      total: completion.end,
                    })}
                    delay={0}
                  />
                )}
              </>
            )}

            {!isProject && (
              <MetaRow
                icon={BusinessChart}
                label={t('pages.goal-detail.header.target.label')}
                isClickable={isEditable}
                isPlaceholder={targetLabel.kind === GoalTargetLabelKind.None}
                onClick={onTargetClick}
              >
                {targetText}
              </MetaRow>
            )}

            <ImpactPickerRow goal={goal} isEditable={isEditable} />

            <StartDatePickerRow goal={goal} isEditable={isEditable} />

            <DeadlinePickerRow goal={goal} isEditable={isEditable} />
          </Styled.MetaContainer>
        </Styled.Header>
      </PageSectionBase>
    </>
  );
};
